<script setup>
  import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const props = defineProps({
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    modelValue: {
      type: Object,
      default: () => ({ name: '' })
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'name'
    },
    trackBy: {
      type: String,
      default: 'id'
    },
    ariaLabel: {
      type: String,
      default: ''
    },
    clearButtonLabel: {
      type: String,
      default: ''
    },
    serverSearch: {
      type: Boolean,
      default: false
    },
    searchTerm: {
      type: String,
      default: ''
    }
  });

  const emit = defineEmits(['update:modelValue', 'clear', 'change', 'input', 'search']);

  const filteredResults = ref([]);
  const displayValue = ref('');
  const showDropdown = ref(false);
  const dropdownRef = ref(null);
  const inputRef = ref(null);

  watch(() => props.options, (newOptions) => {
    filteredResults.value = newOptions || [];
  }, { immediate: true });

  watch(() => props.modelValue, (newValue) => {
    if (newValue?.id && newValue?.name) {
      displayValue.value = newValue.name;
    } else if (!newValue?.id) {
      displayValue.value = '';
    }
  }, { immediate: true });

  const handleInput = (e) => {
    const inputValue = e.target.value;
    displayValue.value = inputValue;

    if (props.serverSearch) {
      filteredResults.value = [];
      emit('search', inputValue);
    } else {
      searchByName(inputValue);
    }

    showDropdown.value = true;
    emit('input', e);
  };

  const handleFocus = () => {
    showDropdown.value = true;
  };

  const searchByName = (inputValue) => {
    if (!inputValue.trim()) {
      filteredResults.value = props.options || [];
      return;
    }
    
    if (!props.options || props.options.length === 0) {
      filteredResults.value = [];
      return;
    }
    
    const normalize = (str) => {
      return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    };
    
    const normalizedInput = normalize(inputValue);
    
    const matchingItems = props.options.filter(item => 
      normalize(item[props.label]).includes(normalizedInput)
    );
    
    filteredResults.value = matchingItems || [];
  };

  const selectItem = (item) => {
    const selected = { 
      id: item[props.trackBy], 
      name: item[props.label] 
    };
    displayValue.value = item[props.label];
    showDropdown.value = false;
    
    emit('update:modelValue', selected);
    emit('change', selected);
  };

  const clear = () => {
    displayValue.value = '';
    filteredResults.value = props.options || [];
    showDropdown.value = false;
    emit('update:modelValue', { name: '' });
    emit('clear');
    emit('change', { name: '' });
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
      showDropdown.value = false;
    }
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  const filteredOptions = computed(() => {
    return filteredResults.value;
  });

  const inputValue = computed({
    get() {
      return displayValue.value;
    },
    set(value) {
      displayValue.value = value;
    }
  });
</script>

<template>
  <div class="dropdown" ref="dropdownRef">
    <div class="input-group">
      <input 
        ref="inputRef"
        :class="['form-control', { 'disabled-select': disabled }]"
        type="text"
        :aria-label="ariaLabel" 
        v-model="inputValue"
        @input="handleInput"
        @focus="handleFocus"
        @keydown.enter.prevent
        :disabled="disabled || loading"
        :placeholder="props.searchTerm && props.options.length > 0 ? `&#x1F50D; ${placeholder} ('${props.searchTerm}' ${props.options.length})` : `&#x1F50D; ${placeholder}`"
        autocomplete="off"
      />
      <button 
        type="button" 
        class="btn btn-outline-secondary btn-no-left-margin" 
        @click="clear"
        :disabled="disabled || !modelValue?.id"
        v-if="props.modelValue?.id"
        :aria-label="clearButtonLabel"
      >
        ×
      </button>
    </div>
    
    <ul 
      class="dropdown-menu" 
      :class="{ 'show': showDropdown && (loading || filteredOptions.length > 0) }"
      style="max-height: 300px; overflow-y: auto;"
    >
      <li v-if="loading" class="text-center py-2">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </li>
      <li v-else-if="filteredOptions.length === 0">
        <span class="dropdown-item-text">No results found</span>
      </li>
      <li 
        v-else
        v-for="item in filteredOptions" 
        :key="item[trackBy]"
      >
        <a 
          class="dropdown-item" 
          href="#"
          @click.prevent="selectItem(item)"
        >
          {{ item[label] }}
        </a>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
  .disabled-select {
    opacity: 0.3 !important;
    background-color: white !important;
  }

  .dropdown {
    position: relative;
    width: 100%;
  }

  .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    display: none;
    min-width: 100%;
    padding: 0.5rem 0;
    margin: 0.125rem 0 0;
    font-size: 1rem;
    color: #212529;
    text-align: left;
    list-style: none;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 0.25rem;
  }

  .dropdown-menu.show {
    display: block;
    width: max-content !important;
    min-width: 100%;
  }

  .dropdown-item {
    display: block;
    width: 100%;
    padding: 0.25rem 1rem;
    clear: both;
    font-weight: 400;
    color: #212529;
    text-align: inherit;
    text-decoration: none;
    white-space: nowrap;
    background-color: transparent;
    border: 0;
    cursor: pointer;
  }

  .dropdown-item:hover,
  .dropdown-item:focus {
    color: #fff;
    background-color: var(--bs-primary, #0d6efd);
    text-decoration: none;
  }

  .dropdown-item-text {
    display: block;
    padding: 0.25rem 1rem;
    color: #6c757d;
  }

  .btn-no-left-margin {
    margin-left: 0 !important;
  }
</style>
